import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { AIChatCitationFragment, StaffAIChatSessionPreviewFragment } from 'types/graphql'
import { v4 as uuidv4 } from 'uuid'

import { AIEntryPoint } from 'src/components/CitizenAIAssistant/CitizenAIAssistantContext'
import useDisclosure, { UseDisclosureReturn } from 'src/hooks/use-disclosure'
import { usePersistentIds } from 'src/hooks/use-persistent-ids'

// Type for the chat storage IDs - unique identifiers for chat sessions
type ChatStorageIds = {
  chatLocalStorageId: string
  chatSessionStorageId: string
}

// Message type for Staff AI Chat
export type StaffAIChatMessage = {
  id: string | number
  text: string
  sender: 'user' | 'ai'
  questionCreatedAt?: string
  responseCreatedAt?: string
  isThinking?: boolean
  citations?: AIChatCitationFragment[]
}

// View mode for the AI Assistant - side panel or full modal
export type StaffAIViewMode = 'side' | 'full'

// Context value type for the Staff AI Assistant
export type StaffAIAssistantContextValue = {
  // Overlay state
  overlay: UseDisclosureReturn
  // Deferred close that can be cancelled by openFullView to prevent flicker
  deferredClose: () => void
  // View mode state - side panel or full modal
  viewMode: StaffAIViewMode
  setViewMode: (mode: StaffAIViewMode) => void
  // Which UI element opened the assistant (pill button vs sidebar button)
  entryPoint: AIEntryPoint
  // Open in side view mode
  openSideView: () => void
  // Open in full modal mode
  openFullView: () => void
  // Maximize from side to full
  maximize: () => void
  // Minimize from full to side
  minimize: () => void
  // Message state - persists across overlay open/close within same React session
  messages: StaffAIChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<StaffAIChatMessage[]>>
  // Typing indicator state
  isAssistantTyping: boolean
  setIsAssistantTyping: React.Dispatch<React.SetStateAction<boolean>>

  // User text input state (persists across view switches)
  userTextInput: string
  setUserTextInput: React.Dispatch<React.SetStateAction<string>>
  // Search query for sidebar (persists across view switches)
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>

  // Session management for past conversations
  activeSessionId: number | null
  chatStorageIds: ChatStorageIds
  isLoadingSession: boolean
  setActiveSessionId: (sessionId: number | null) => void
  setIsLoadingSession: (loading: boolean) => void
  startNewConversation: () => void
}

// Context for controlling the Staff AI Assistant overlay and persisting conversation
// Using React Context because:
// - We plan to open this from multiple places (Navbar, keyboard shortcut, etc.)
// - Messages should persist across overlay open/close within the same browser session
// - Users can start a fresh chat when they want via "New Chat" button
const StaffAIAssistantContext = createContext<StaffAIAssistantContextValue | null>(null)

// Returns null if used outside the provider - callers should handle the null case
export const useStaffAIAssistant = (): StaffAIAssistantContextValue | null => {
  return useContext(StaffAIAssistantContext)
}

type Props = {
  children: React.ReactNode
}

/**
 * Generates new chat storage IDs for a fresh conversation.
 * Uses browserId for local storage ID and a new UUID for session storage ID.
 */
const useNewChatStorageIds = (browserId: string): (() => ChatStorageIds) => {
  return useCallback(() => {
    const uniqueId = uuidv4()
    return {
      chatLocalStorageId: `${browserId}-chat-${uniqueId}`,
      chatSessionStorageId: `${uniqueId}-chat`,
    }
  }, [browserId])
}

export const StaffAIAssistantProvider = ({ children }: Props) => {
  const overlay = useDisclosure()
  const [viewMode, setViewMode] = useState<StaffAIViewMode>('side')
  const [entryPoint, setEntryPoint] = useState<AIEntryPoint>('InProductPill')
  const [messages, setMessages] = useState<StaffAIChatMessage[]>([])
  const [isAssistantTyping, setIsAssistantTyping] = useState(false)
  const [userTextInput, setUserTextInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const { browserId } = usePersistentIds()
  const generateNewChatStorageIds = useNewChatStorageIds(browserId)

  // Chat storage IDs for the current session
  // Generate new IDs when there's no active session (new conversation)
  const [chatStorageIds, setChatStorageIds] = useState<ChatStorageIds>(() =>
    generateNewChatStorageIds()
  )

  // Start a new conversation - clears messages, active session, and generates new storage IDs
  const startNewConversation = useCallback(() => {
    setMessages([])
    setIsAssistantTyping(false)
    setUserTextInput('')
    setActiveSessionId(null)
    setChatStorageIds(generateNewChatStorageIds())
    setIsLoadingSession(false)
  }, [generateNewChatStorageIds])

  const handleSetActiveSessionId = useCallback((sessionId: number | null) => {
    setActiveSessionId(sessionId)
    if (sessionId === null) {
      // When clearing the active session, also clear loading state
      setIsLoadingSession(false)
    }
  }, [])

  // Deferred close: schedules close for the next tick so openFullView can cancel it.
  // This prevents flicker when the sidebar button click triggers ContentAreaOverlay's
  // capture-phase click-outside handler (which fires before our React onClick).
  const pendingCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const deferredClose = useCallback(() => {
    if (pendingCloseRef.current !== null) {
      clearTimeout(pendingCloseRef.current)
    }
    pendingCloseRef.current = setTimeout(() => {
      pendingCloseRef.current = null
      overlay.close()
    }, 0)
  }, [overlay])

  useEffect(() => {
    return () => {
      if (pendingCloseRef.current !== null) {
        clearTimeout(pendingCloseRef.current)
      }
    }
  }, [])

  // Open the AI assistant in side view mode (via pill button)
  const openSideView = useCallback(() => {
    setEntryPoint('InProductPill')
    setViewMode('side')
    overlay.open()
  }, [overlay])

  // Open the AI assistant in full modal mode (via sidebar button)
  const openFullView = useCallback(() => {
    if (pendingCloseRef.current !== null) {
      clearTimeout(pendingCloseRef.current)
      pendingCloseRef.current = null
    }
    setEntryPoint('InProductSidebar')
    setViewMode('full')
    overlay.open()
  }, [overlay])

  // Maximize from side to full modal view
  const maximize = useCallback(() => {
    setViewMode('full')
  }, [])

  // Minimize from full modal to side view
  const minimize = useCallback(() => {
    setViewMode('side')
  }, [])

  const value = useMemo<StaffAIAssistantContextValue>(
    () => ({
      overlay,
      deferredClose,
      viewMode,
      setViewMode,
      entryPoint,
      openSideView,
      openFullView,
      maximize,
      minimize,
      messages,
      setMessages,
      isAssistantTyping,
      setIsAssistantTyping,
      userTextInput,
      setUserTextInput,
      searchQuery,
      setSearchQuery,
      activeSessionId,
      chatStorageIds,
      isLoadingSession,
      setActiveSessionId: handleSetActiveSessionId,
      setIsLoadingSession,
      startNewConversation,
    }),
    [
      overlay,
      deferredClose,
      viewMode,
      entryPoint,
      openSideView,
      openFullView,
      maximize,
      minimize,
      messages,
      isAssistantTyping,
      userTextInput,
      searchQuery,
      activeSessionId,
      chatStorageIds,
      isLoadingSession,
      handleSetActiveSessionId,
      startNewConversation,
    ]
  )

  return (
    <StaffAIAssistantContext.Provider value={value}>{children}</StaffAIAssistantContext.Provider>
  )
}

// Helper hook to get session preview info for display
export const useSessionPreviewTitle = (
  session: StaffAIChatSessionPreviewFragment | null
): string => {
  if (!session?.previewChat?.question) {
    return 'New Conversation'
  }
  // Truncate the question if it's too long
  const question = session.previewChat.question
  if (question.length > 50) {
    return `${question.substring(0, 47)}...`
  }
  return question
}
